import Image from "next/image";
// A locale-less `/ej-health/…` href resolves to the DEFAULT locale, which
// every page in this vertical `notFound()`s — see `arrow-button.tsx`.
import { Link } from "@/i18n/routing";
import { EJHEALTH_COLORS, EJHEALTH_TYPE } from "@/lib/theme/ej-health";
import { CategoryEyebrow } from "../category-badge";
import { AuthorChip } from "../author-chip";
import { EJHEALTH_ARTICLE_ROUTE } from "@/lib/routes";
import type { ArticleListItem } from "@/fixtures/ej-health/medical-news-fixtures";

interface Props {
  item: ArticleListItem;
  /** Required — see `../sidebar-thumb.tsx`. Defaults to the article page,
   *  which is what every card in every listing opens. */
  href?: string;
}

/** Vertical listing card — 16:9 image on top, title + excerpt + meta below. */
export function ListingCard({ item, href = EJHEALTH_ARTICLE_ROUTE }: Props) {
  return (
    <Link href={href} className="group hkej-tap flex flex-col gap-3">
      <div
        className="relative w-full overflow-hidden"
        style={{
          aspectRatio: 16 / 9,
          backgroundColor: EJHEALTH_COLORS.surfaceAlt,
        }}
      >
        <Image
          src={item.img}
          alt={item.title}
          fill
          className="object-contain object-center transition-transform duration-500 group-hover:scale-[1.03]"
          sizes="(max-width: 640px) 100vw, 400px"
          unoptimized
        />
      </div>
      <div className="flex flex-col gap-2">
        {item.category && <CategoryEyebrow label={item.category} />}
        <h3
          className="hkej-tap-title line-clamp-2"
          style={{
            ...EJHEALTH_TYPE.cardTitleSm,
            color: EJHEALTH_COLORS.dark,
            fontSize: 18,
            lineHeight: "26px",
          }}
        >
          {item.title}
        </h3>
        <p
          className="line-clamp-2"
          style={{
            ...EJHEALTH_TYPE.body,
            color: EJHEALTH_COLORS.textBody,
            fontSize: 14,
            lineHeight: "22px",
          }}
        >
          {item.excerpt}
        </p>
        {/* Bylined categories (醫生專欄, 醫健一分鐘, 醫學通識, 食出健康,
            運動懶人包, 健康財富) carry the doctor's avatar and name on every
            article; the rest have no `author` and this collapses. */}
        <div className="flex flex-col gap-1.5">
          {item.author && <AuthorChip author={item.author} size={20} />}
          <span
            style={{ ...EJHEALTH_TYPE.meta, color: EJHEALTH_COLORS.textMuted }}
          >
            {item.time}
          </span>
        </div>
      </div>
    </Link>
  );
}
